import { type MutableRefObject, type ReactNode, createContext, useContext, useRef } from "react";

/**
 * Selector selection context type.
 * Provides a mutable ref that TableRenderer writes selected transfers to,
 * and dialog footer buttons read from when dispatching Add/Set actions.
 *
 * Uses a ref instead of state to avoid re-renders when selection changes -
 * the value is only read at dispatch time.
 */
export interface SelectorSelectionContextType {
	/** Mutable ref holding the currently selected transfers */
	selectedTransfersRef: MutableRefObject<unknown[]>;
	/** The owner transfer for the selector (who owns the relation) */
	ownerTransfer?: unknown;
}

const SelectorSelectionContext = createContext<SelectorSelectionContextType | null>(null);

/**
 * Hook to access selector selection context.
 *
 * @returns SelectorSelectionContextType
 * @throws Error if used outside SelectorSelectionProvider
 */
export function useSelectorSelection(): SelectorSelectionContextType {
	const context = useContext(SelectorSelectionContext);
	if (!context) {
		throw new Error("useSelectorSelection must be used within SelectorSelectionProvider");
	}
	return context;
}

/**
 * Hook to access selector selection context (optional).
 * Returns null when not inside a selector dialog.
 *
 * @returns SelectorSelectionContextType | null
 */
export function useSelectorSelectionOptional(): SelectorSelectionContextType | null {
	return useContext(SelectorSelectionContext);
}

/**
 * Props for SelectorSelectionProvider.
 */
export interface SelectorSelectionProviderProps {
	children: ReactNode;
	/** The owner transfer for the selector relation */
	ownerTransfer?: unknown;
}

/**
 * Provider for selector selection state.
 * Wraps selector dialog content to enable sharing selection state
 * between TableRenderer (writer) and dialog footer buttons (reader).
 */
export function SelectorSelectionProvider({ children, ownerTransfer }: SelectorSelectionProviderProps) {
	const selectedTransfersRef = useRef<unknown[]>([]);

	const value: SelectorSelectionContextType = { selectedTransfersRef, ownerTransfer };

	return <SelectorSelectionContext.Provider value={value}>{children}</SelectorSelectionContext.Provider>;
}
